import { useMemo, useRef } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { isAbortError, useApi } from '@/api/client';
import type {
  ReportParameterDefinition,
  ReportParameterValue,
  ResolvedParameterValues
} from '@/types';
import { unsetIdentityParameters, upstreamParameterNames } from './identityParameter';
import { useDebounced } from './useDebounced';

/**
 * How far one parameter's value-list lookup has got.
 *
 * `resolving` and `resolved` are the pair that matters, and collapsing them — which is what asking
 * "does it have options?" did — makes a field mid-lookup indistinguishable from one the report has
 * already answered "none" for. The honest rendering of those two is not the same: one is a
 * progress indicator, the other is a free-text box and an explanation.
 *
 * `failed` is the case that must never be described as a report with nothing to offer. It covers
 * an error AND a response that came back with nothing whatsoever — not "this parameter has no
 * values for those choices", but "no parameter did". Worth telling apart because the endpoint
 * deliberately answers 200-with-an-empty-list for every failure it can have: a report server that
 * is unreachable, a report the portal's account may not read, and a genuinely empty answer are
 * indistinguishable from here. A report that got as far as being worth resolving has at least one
 * parameter drawing on a dataset, so a completely empty response is far more likely to be a lookup
 * that did not happen than a report with nothing to say — and blaming the report for it sends the
 * reader off to check their own choices for a fault that is not theirs.
 *
 * `idle` covers the parameters no lookup will ever cover: a date, a number, a static list from the
 * catalogue. They must never flicker because some other field on the form is resolving.
 */
export type LookupState = 'idle' | 'resolving' | 'resolved' | 'failed';

/** Whether this parameter's list is one the report server has to be asked for. */
function awaitsLookup(def: ReportParameterDefinition): boolean {
  return def.dynamicValidValues || (def.dependencies?.length ?? 0) > 0;
}

/**
 * Fetches the value lists the catalog cannot hold, once the form is already on screen.
 *
 * Only the parameters that something else depends on go into the request, and they are what the
 * query is keyed on. That is what makes the cascade work without making it expensive: picking a
 * View re-resolves Country and Head Office because ViewId is upstream of both, while typing in a
 * free-text parameter that nothing depends on costs nothing at all.
 *
 * Three things keep the form from appearing to revert while that is in flight:
 *
 * - `keepPreviousData`, so a changed key does not blank every list on the form for the 1.6s the
 *   next answer takes. Without it `data` is undefined the moment the key moves, every dynamic
 *   field loses its options at once, and each one falls back to rendering its raw stored id — on
 *   /Sales/Scope 3 the Country Code dropdown reading "UK" became a text box reading "1".
 * - the key is SORTED, so clicking around does not invent new keys for choices already answered.
 *   Re-picking a value you already had used to produce a different string, hence a different query
 *   key, hence a cache miss and another 1.6s / 312KB round trip for an unchanged answer.
 * - the request the answer came from is carried back with it, so "did we supply this parameter
 *   ourselves?" describes the response in hand rather than the form a keystroke later.
 *
 * A superseded lookup is cancelled rather than left to finish. Measured in production, five of
 * these overlapping took 15.9s / 15.6s / 9.0s / 7.6s / 7.0s and all landed within 200ms of each
 * other, against 1.0-1.9s for the same call made alone: MANDW01 serialises them, so a lookup for a
 * View the user has already clicked past is not merely wasted, it is sitting in front of the one
 * they are waiting for. `keepPreviousData` above is what made this easy to miss — the stale answer
 * stays on screen looking harmless while its replacement queues behind a request for choices
 * nobody made any more.
 */
export function useResolvedValues(
  reportPath: string | undefined,
  definitions: ReportParameterDefinition[],
  values: ReportParameterValue[]
) {
  const api = useApi();

  // Already in the cache — App.tsx loads it before anything renders, so this costs no round trip.
  const { data: me } = useQuery({ queryKey: ['me'], queryFn: api.me, staleTime: Infinity });

  const upstreamNames = useMemo(() => upstreamParameterNames(definitions), [definitions]);

  // Only reports that actually have a list to fetch. A report whose parameters are dates, numbers
  // and free text has nothing the server could add, and asking anyway would spend 1-2s of MANDW01's
  // time per page view to be told so — the catalog already knows the answer is no.
  const worthResolving = useMemo(() => definitions.some(awaitsLookup), [definitions]);

  /**
   * Which identity parameters this lookup is standing in for the user on, and with what.
   *
   * This used to say the seed was deliberately never fed back through onChange — that asking the
   * report to LIST values as somebody and storing whose data the subscription RUNS as were
   * different acts, and only the first was safe to do unasked. Production disproved it. A
   * subscription whose lists were resolved as a real person, but which stored nothing for the
   * identity parameter, resolves it at run time as `KEY\svc_reportportal` instead — for whom the
   * Country Code list has 0 entries — and fails every run with "Default value or value provided
   * for the report parameter 'CountryCode' is not a valid value". Offering values the subscription
   * could not then use was the whole of the bug. The create form now writes the account in (see
   * CreateSubscription and withIdentityAccount), so on that form this map is empty by the time it
   * matters: a parameter that has a value is not a parameter that needs seeding.
   *
   * The seed still exists for the two cases where nothing is stored — an existing subscription
   * being edited, which is repaired on request rather than silently (see ParametersEditor), and a
   * create whose identity value has been cleared by hand. In both, the lists on screen would
   * otherwise be empty, and an empty list is the one thing this hook must never let a report be
   * blamed for.
   */
  const seededNames = useMemo(() => {
    const map = new Map<string, string>();
    const account = me?.windowsAccountName?.trim();
    if (!account) return map;
    for (const name of unsetIdentityParameters(definitions, upstreamNames, values)) map.set(name, account);
    return map;
  }, [definitions, upstreamNames, values, me]);

  /**
   * What the request carries: the upstream values the user has chosen, plus any seed.
   *
   * Sorted by name so the key describes the CHOICES rather than the order they were made in.
   */
  const lookupValues = useMemo(() => {
    const chosen = values
      .filter(v => upstreamNames.has(v.name) && v.values.length > 0)
      .map(v => ({ name: v.name, values: v.values }));
    const seeds = [...seededNames].map(([name, account]) => ({ name, values: [account] }));
    return [...chosen, ...seeds].sort((a, b) => a.name.localeCompare(b.name));
  }, [values, upstreamNames, seededNames]);

  // Keyed on the upstream choices only, so re-renders from unrelated edits do not refetch.
  const key = JSON.stringify(lookupValues);
  const debouncedKey = useDebounced(key, 300);

  /**
   * Everything this form already holds, upstream or not — sent so the server cannot cap or filter
   * away the very entries these fields are rendering.
   *
   * Wider than `lookupValues` on purpose. Head Office is downstream of everything and upstream of
   * nothing, so it appears in no cascade request at all; it is also the one parameter whose list is
   * long enough (5,846 on /Sales/Scope 3) for the server's 500-entry cap to bite, which is exactly
   * how subscription 6cf6e03f came to display its perfectly valid Head Office 199159 as
   * "(no longer offered)".
   *
   * Blanks and query-bound parameters are dropped: a data-driven subscription's `fromField` value
   * is a column name rather than a value, and a deliberate blank matches no option by construction.
   */
  const selectedValues = useMemo(
    () => values
      .filter(v => !v.fromField)
      .map(v => ({ name: v.name, values: v.values.filter(x => x.trim() !== '') }))
      .filter(v => v.values.length > 0),
    [values]
  );

  /**
   * Carried by ref, and NOT by query key — the same rule the key comment above states, applied to
   * the one field that would otherwise break it.
   *
   * Keying on this would refetch the whole cascade every time any field on the form changed, which
   * is the cost the sorted upstream-only key exists to avoid: 1.2-2.0s and ~312KB per lookup, and
   * MANDW01 answers them one at a time. A ref lets the body carry the current selection at the
   * moment a fetch actually runs while the key still describes only what the report server is being
   * asked to resolve.
   *
   * The trade is deliberate, and worth stating exactly rather than implying it is free. The body
   * that counts is the one belonging to the fetch that actually ran, so two cases keep the old
   * wording for a while: a value changed while a lookup is in flight is not pinned in THAT answer,
   * and — because this key is the same for any two subscriptions of the same report with the same
   * upstream choices — a second subscription opened inside react-query's 120s staleTime reuses the
   * first one's list, pins and all. Both resolve on the next real lookup, and both are bounded by
   * that staleTime.
   *
   * The alternative is worse in the ordinary case, which is why it was not taken. Keying on this
   * refetches the whole cascade whenever any field changes: 1.2-2.0s and ~312KB, serialised by
   * MANDW01, for every keystroke in every free-text box — including the fields the sorted
   * upstream-only key was introduced to make free. If the residual ever needs closing, the cheap
   * shape is to scope the key by the subscription being edited rather than by its values, since the
   * resolver's own 10-minute cache makes the extra round trip a ~9ms hit rather than a fresh
   * warehouse query.
   */
  const selectedRef = useRef(selectedValues);
  selectedRef.current = selectedValues;

  const { data, isFetching, isError, error, isSuccess } = useQuery({
    queryKey: ['parameter-values', reportPath, debouncedKey],
    // Reading `signal` here is not incidental: react-query only cancels an in-flight fetch when the
    // query function has consumed the signal (`abortSignalConsumed`). Without it the key can move,
    // the observer can drop the old query, and the request it started still runs to completion on
    // MANDW01 — which is exactly what the timings above are. Consuming it also means the
    // cancellation reverts the query rather than recording an error, so a request called off never
    // becomes a `failed` lookup.
    queryFn: async ({ signal }) => {
      const requested = JSON.parse(debouncedKey) as ReportParameterValue[];
      const results = await api.resolveParameterValues(
        { path: reportPath!, values: requested, selected: selectedRef.current }, signal);
      // Carried together deliberately: `results` can only be read correctly against the request
      // that produced it, and under keepPreviousData that is not the current form state.
      return { requested, results };
    },
    enabled: Boolean(reportPath) && worthResolving,
    staleTime: 120_000,
    // The endpoint answers 200-with-nothing rather than failing, so a retry only ever repeats an
    // expensive call that already gave its answer.
    retry: false,
    // Keeps the lists on screen while the next answer is fetched. Without this every dynamic
    // field blanks the instant the key moves and re-renders as its raw stored id.
    placeholderData: keepPreviousData
  });

  const resolved = useMemo(() => {
    const map: Record<string, ResolvedParameterValues> = {};
    for (const r of data?.results ?? []) map[r.name] = r;
    return map;
  }, [data]);

  /**
   * The names whose values went INTO the request that produced `resolved`.
   *
   * The report server echoes a supplied value back as that parameter's "default": post ViewId=91
   * and ViewId comes back with DefaultValues [91]. Read naively that says the report defaults to
   * whatever this subscription already stores, which is the one claim the editor must never make.
   * Read off the request the response carries, so it describes the answer in hand rather than the
   * values as they are a keystroke later. It covers the seeded identity parameters too: post
   * User=dmorris and the server obligingly reports that the report's default for User is dmorris,
   * which would be the portal quoting itself back to the user as though the report had said it.
   */
  const suppliedNames = useMemo(
    () => new Set((data?.requested ?? []).map(v => v.name)),
    [data]
  );

  // The debounce window counts as resolving. It is only 300ms, but it is 300ms in which the user
  // has typed something and the form shows no sign of having noticed — which is exactly the
  // "it doesn't do anything here, it eventually does" half of the complaint.
  const settling = key !== debouncedKey;

  /**
   * A lookup that was called off, which is not a lookup that failed.
   *
   * `failed` says the report server was asked and could not answer, and the field renders that as
   * a fault to go and investigate. A cancelled request said nothing at all about the report server
   * — it was abandoned because the choices it was resolving are no longer the ones on screen, and
   * the request for the current ones is what comes next. Cancelling through the query's own signal
   * should already keep this out of `isError` (react-query reverts a cancelled query rather than
   * failing it), so this is the belt to that braces: the cost of being wrong is an alarming and
   * untrue message on a form whose real answer is already in flight.
   */
  const abandoned = isError && isAbortError(error);

  const state: LookupState =
    !worthResolving || !reportPath ? 'idle'
      : settling || isFetching || abandoned ? 'resolving'
        : isError || (isSuccess && (data?.results.length ?? 0) === 0) ? 'failed'
          : isSuccess ? 'resolved'
            : 'idle';

  /**
   * A parameter with nothing to look up is idle whatever the shared query is doing.
   *
   * One `useQuery` holds every parameter's list, so a form-wide "is it resolving?" made a static
   * dropdown announce it was waiting because some unrelated field was. Only the fields actually
   * waiting on a list may say they are waiting.
   */
  function stateFor(def: ReportParameterDefinition): LookupState {
    return awaitsLookup(def) ? state : 'idle';
  }

  return { resolved, stateFor, suppliedNames, lookupValues, seededNames };
}
